/**
 * ModificationSettingsSubTab — Configure Cancel+New vs edit-in-place per modification type.
 * Renders inside AccountingConfigPanel as the "Modification Settings" sub-tab.
 *
 * KAN-225c-A: Trade Modification v2
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { modificationService } from '../../services/api/modificationService';
import AlertModal from '../common/AlertModal';
import type { ModificationConfig, ConfigurableModificationType } from '../../types/modification';
import type { GroupingsConfig } from '../../types/groupings';
import { groupingsService } from '../../services/api/groupingsService';
import './AccountingConfigPanel.css';

interface ModificationSettingsSubTabProps {
  clientId: string;
}

const DEFAULT_CONFIG: ModificationConfig = {
  modificationTypes: {
    Rollover: { cancelAndNew: false },
    SettlementTypeChange: { cancelAndNew: false },
    GroupingChange: { cancelAndNew: false },
  },
};

const TYPE_ROWS: ConfigurableModificationType[] = ['Rollover', 'SettlementTypeChange', 'GroupingChange'];

const TYPE_I18N_KEY: Record<ConfigurableModificationType, string> = {
  Rollover: 'rollover',
  SettlementTypeChange: 'settlementTypeChange',
  GroupingChange: 'groupingChange',
};

const ModificationSettingsSubTab: React.FC<ModificationSettingsSubTabProps> = ({ clientId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'client_admin';

  const [config, setConfig] = useState<ModificationConfig>(DEFAULT_CONFIG);
  const [savedConfig, setSavedConfig] = useState<ModificationConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  // ============================================
  // Data Loading
  // ============================================

  const loadConfig = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const response = await modificationService.getModificationConfig(clientId);
      if (response.success && response.data) {
        const merged: ModificationConfig = {
          modificationTypes: {
            ...DEFAULT_CONFIG.modificationTypes,
            ...response.data.modificationTypes,
          },
        };
        setConfig(merged);
        setSavedConfig(merged);
      }
    } catch (err) {
      console.error('Error loading modification config:', err);
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: t('common.error'),
        message: t('settings.modificationSettings.loadError'),
      });
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadConfig();
    groupingsService.getGroupings(clientId).then(setGroupingsConfig).catch(() => { /* ignore — label falls back to generic */ });
  }, [loadConfig]);

  // ============================================
  // Handlers
  // ============================================

  const handleToggle = (type: ConfigurableModificationType, value: boolean) => {
    setConfig((prev) => ({
      ...prev,
      modificationTypes: {
        ...prev.modificationTypes,
        [type]: { cancelAndNew: value },
      },
    }));
  };

  const hasChanges =
    JSON.stringify(config) !== JSON.stringify(savedConfig);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await modificationService.saveModificationConfig(clientId, config);
      if (response.success && response.data) {
        const saved: ModificationConfig = {
          modificationTypes: {
            ...DEFAULT_CONFIG.modificationTypes,
            ...response.data.modificationTypes,
          },
        };
        setConfig(saved);
        setSavedConfig(saved);
        setAlertModal({
          isOpen: true,
          type: 'success',
          title: t('common.success'),
          message: t('settings.modificationSettings.saveSuccess'),
        });
      }
    } catch (err) {
      console.error('Error saving modification config:', err);
      // Revert to last saved state on error
      setConfig(savedConfig);
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: t('common.error'),
        message: t('settings.modificationSettings.saveError'),
      });
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // Render
  // ============================================

  const getModTypeLabel = (type: ConfigurableModificationType, tKey: string): string => {
    if (type === 'GroupingChange' && groupingsConfig) {
      const labels = [
        groupingsConfig.grouping1Label,
        groupingsConfig.grouping2Label,
        groupingsConfig.grouping3Label,
      ].filter(Boolean);
      if (labels.length > 0) {
        const base = t(`modifications.types.${tKey}`);
        return `${base.split(' ').slice(0, -1).join(' ')} ${labels.join(' / ')}`;
      }
    }
    return t(`modifications.types.${tKey}`);
  };

  const getModTypeDescription = (type: ConfigurableModificationType, tKey: string): string => {
    const base = t(`settings.modificationSettings.typeDescriptions.${tKey}`);
    if (type === 'GroupingChange' && groupingsConfig) {
      const labels = [
        groupingsConfig.grouping1Label,
        groupingsConfig.grouping2Label,
        groupingsConfig.grouping3Label,
      ].filter(Boolean);
      if (labels.length > 0) {
        return base.replace('G1/G2/G3', labels.join(' / '));
      }
    }
    return base;
  };

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="spinner" />
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="accounting-config-content">
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal((prev) => ({ ...prev, isOpen: false }))}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />

      <div className="config-section">
        <p style={{ marginTop: 0, marginBottom: '1.25rem', color: 'var(--text-secondary, #6b7280)' }}>
          {t('settings.modificationSettings.description')}
        </p>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color, #e5e7eb)', fontWeight: 600 }}>
                {t('settings.modificationSettings.columnType')}
              </th>
              <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color, #e5e7eb)', fontWeight: 600, width: '140px' }}>
                {t('settings.modificationSettings.columnCancelAndNew')}
              </th>
              <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color, #e5e7eb)', fontWeight: 600 }}>
                {t('settings.modificationSettings.columnDescription')}
              </th>
            </tr>
          </thead>
          <tbody>
            {TYPE_ROWS.map((type) => {
              const tKey = TYPE_I18N_KEY[type];
              const isOn = config.modificationTypes[type]?.cancelAndNew ?? false;
              return (
                <tr key={type}>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color, #e5e7eb)', fontWeight: 500 }}>
                    {getModTypeLabel(type, tKey)}
                  </td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color, #e5e7eb)', textAlign: 'center' }}>
                    <label className="toggle-switch" style={{ display: 'inline-flex' }}>
                      <input
                        type="checkbox"
                        checked={isOn}
                        disabled={!isAdmin}
                        onChange={(e) => handleToggle(type, e.target.checked)}
                      />
                      <span className="slider" />
                    </label>
                  </td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color, #e5e7eb)', color: 'var(--text-secondary, #6b7280)', fontSize: '0.875rem' }}>
                    {getModTypeDescription(type, tKey)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <p style={{ marginTop: '1rem', fontSize: '0.8125rem', color: 'var(--text-secondary, #6b7280)' }}>
          {t('settings.modificationSettings.nonConfigurableNote')}
        </p>
      </div>

      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? t('settings.modificationSettings.saving') : t('settings.modificationSettings.saveChanges')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ModificationSettingsSubTab;
